import { useState, useEffect, useCallback } from 'react';
import { Layers, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { useMarinaModulesRefresh } from '../context/MarinaModulesRefreshContext';

interface MarinaModuleRow {
  id: number;
  module: string;
  is_enabled: boolean;
  config?: Record<string, unknown>;
}

/** Optional AI modules: only parking (car + caravan/MH) — sauna/bikes/etc. use Additional services. */
const MODULE_KEYS = ['parking'] as const;
type ModuleKey = (typeof MODULE_KEYS)[number];

export function ModulesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const { bump: refreshModuleDependentUi } = useMarinaModulesRefresh();
  const [rows, setRows] = useState<MarinaModuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getModules();
      setRows((data.modules ?? []) as unknown as MarinaModuleRow[]);
    } catch (err) {
      if (err instanceof ApiError) toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [toast, activeMarinaId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const rowFor = (key: ModuleKey): MarinaModuleRow | undefined =>
    rows.find((r) => r.module === key);

  const enabledFor = (key: ModuleKey): boolean => rowFor(key)?.is_enabled ?? false;

  const toggle = async (key: ModuleKey, next: boolean) => {
    if (saving) return;
    setSaving(key);
    try {
      await api.updateModule(key, { is_enabled: next });
      toast.success(t('modules.updated'));
      await fetchModules();
      refreshModuleDependentUi();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Layers className="w-6 h-6 text-brand-600" />
          {t('modules.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('modules.subtitle')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {MODULE_KEYS.map((key) => {
          const enabled = enabledFor(key);
          const busy = saving === key;
          const showParkingHint = key === 'parking' && enabled;

          return (
            <div
              key={key}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
            >
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 gap-3">
                <span className="text-sm font-semibold text-gray-800">
                  {t(`modules.names.${key}`)}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={busy}
                  onClick={() => toggle(key, !enabled)}
                  className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:opacity-50 ${
                    enabled ? 'bg-brand-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 mt-1 ml-1 rounded-full bg-white shadow transition-transform ${
                      enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              <div className="p-4 space-y-2 flex-1">
                <p className="text-sm text-gray-600 leading-relaxed">{t(`modules.descriptions.${key}`)}</p>
                {!rowFor(key) && (
                  <p className="text-xs text-gray-500">{t('modules.notProvisioned')}</p>
                )}
                {showParkingHint && (
                  <div className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{t('modules.parkingHint')}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
